import { resolveApiFetchUrl } from "../../../services/ApiBaseUrl";
import { apiFetch } from "../../../services/apiFetch";
import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";

/** GET /ext/social/data-deletion/status?confirmation_code=<code> — no auth required */
const DATA_DELETION_STATUS_PATH = "/ext/social/data-deletion/status";
/** POST /home/user/data-deletion/request — no auth; email-based deletion request */
const DATA_DELETION_EMAIL_REQUEST_PATH = "/home/user/data-deletion/request";

const DeletionStatusService = {
    /**
     * Look up data deletion status by confirmation code.
     * @param {string} confirmationCode
     * @returns {Promise<{ notFound: true }|{
     *   confirmation_code: string,
     *   channel: string,
     *   status: string,
     *   requested_at: string,
     *   updated_at: string,
     *   message?: string
     * }>}
     */
    getDataDeletionStatus: async (confirmationCode) => {
        const code = String(confirmationCode || "").trim();
        if (!code) {
            throw new Error("confirmation_code is required");
        }

        const query = `confirmation_code=${encodeURIComponent(code)}`;
        const url = `${DATA_DELETION_STATUS_PATH}?${query}`;

        try {
            const response = await apiFetch(resolveApiFetchUrl(url), {
                method: "GET",
                headers: { Accept: "application/json" },
            });

            if (response.status === 404) {
                return { notFound: true };
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const text = await response.text();
            if (!text) {
                return { notFound: true };
            }

            return JSON.parse(text);
        } catch (error) {
            Logger.error("Error fetching data deletion status:", error);
            throw error;
        }
    },

    /**
     * Request data deletion by email (no login). Sends a validation email with a confirmation link.
     * @param {string} email
     * @param {{ platform?: string, appId?: string }} [options]
     * Optional `platform=google`, `platform=apple`, `platform=amazon`, or `platform=samsung`,
     * and `appId=word-wheel-quest`.
     * @returns {Promise<{ code: string, message: string }>}
     */
    requestDataDeletionByEmail: async (email, { platform, appId } = {}) => {
        const trimmedEmail = String(email || "").trim();
        if (!trimmedEmail) {
            throw new Error("email is required");
        }

        try {
            const body = { email: trimmedEmail };
            if (platform) {
                body.platform = platform;
            }
            if (appId) {
                body.appId = appId;
            }
            const response = await ApiService.postDataOptionalAuth(
                DATA_DELETION_EMAIL_REQUEST_PATH,
                body,
            );

            if (response?.code === "FAILURE") {
                throw new Error(response.message || "Request failed");
            }

            if (response?.code !== "SUCCESS") {
                throw new Error(response?.message || "Request failed");
            }

            return response;
        } catch (error) {
            Logger.error("Error requesting data deletion by email:", error);
            throw error;
        }
    },
};

export default DeletionStatusService;
